// lib/policy/defaultpolicy.ts
// BIDIX Auction Engine v2.2 - Default Policy Values
// Version: 2.2
// Last Updated: 2025-01-28

import { Policy } from "./policy";
import { PropertyType } from "@/lib/core/types";

const defaultPolicy: Policy = {
  version: "2.2",
  updatedAt: "2025-11-13",

  /* ======================================================
   * 1) Valuation (시세 평가 정책)
   * ====================================================== */
  valuation: {
    /** 감정가 대비 FMV 초기 비율 (유형별 차등 적용) */
    baseFMVRatePerType: {
      [PropertyType.Apartment]: 0.98,
      [PropertyType.Villa]: 0.92,
      [PropertyType.Officetel]: 0.96,
      [PropertyType.MultiHouse]: 0.9,
      [PropertyType.Detached]: 0.88,
      [PropertyType.ResidentialLand]: 0.85,

      // 주거용 (크롤링 추가)
      [PropertyType.MultiFamily]: 0.91, // 다세대: Villa와 유사
      [PropertyType.RowHouse]: 0.91, // 연립주택: Villa와 유사
      [PropertyType.MixedUse]: 0.94, // 주상복합: Officetel과 유사

      [PropertyType.Store]: 0.9,
      [PropertyType.Office]: 0.92,
      [PropertyType.Factory]: 0.82,
      [PropertyType.Warehouse]: 0.8,
      [PropertyType.CommercialLand]: 0.88,

      // 상업용 (크롤링 추가)
      [PropertyType.StoreHouse]: 0.89, // 상가주택: Store와 유사
      [PropertyType.NeighborhoodFacility]: 0.88, // 근린생활시설: Store와 유사
      [PropertyType.RetailFacility]: 0.89, // 판매시설: Store와 유사
      [PropertyType.SportsFacility]: 0.85, // 운동시설: Factory와 유사
      [PropertyType.LodgingFacility]: 0.9, // 숙박시설: Store와 유사
    },

    /** FMV 변동성 (시장 상황 랜덤 ±%) */
    fmvVolatilityRange: { min: -0.05, max: 0.03 }, // -5% ~ +3%

    /** FMV 최종 보정 (감정가 대비 비율 제한) */
    fmvClamp: { min: 0.85, max: 1.12 },

    /**
     * Exit Price = FMV × exitDiscountRate × futureRecoveryFactor
     * - 보수적 매각: 96%
     */
    exitDiscountRate: 0.96,

    /**
     * 미래 시세 회복 계수
     * - 기본 1.02 → 2%~3% 시세 회복 기대치 반영
     */
    futureRecoveryFactor: 1.025,

    /** 1회차 최저입찰가 = 감정가 × initialMinBidRate */
    initialMinBidRate: 0.7,

    /** 유찰 1회마다 감정가 대비 최저가 하락 비율 */
    minBidReductionRate: 0.8, // -20%씩 하락

    /** 권장 입찰가 범위 = 목표 대비 ±% */
    recommendedRangeRatio: { min: 0.97, max: 1.03 }, // -3% ~ +3%
  },

  /* ======================================================
   * 2) Rights (권리/명도 정책)
   * ====================================================== */
  rights: {
    evictionBaseCost: 800000,
    evictionRiskWeight: 0.6,
    protectedTenantExtra: 12000000,
  },

  /* ======================================================
   * 3) Cost (취득/보유/대출 정책)
   * ====================================================== */
  cost: {
    // 하위 호환성 유지 (deprecated)
    acquisitionTaxRate: 0.045,

    /** 매물 유형별 취득세율 (1주택 기준)
     * - 주거용: 1.1% (1주택 기준)
     * - 오피스텔: 4.6%
     * - 상가/토지: 4.6%
     */
    acquisitionTaxRatePerType: {
      // 주거용 (1주택 기준 1.1%)
      [PropertyType.Apartment]: 0.011, // 아파트: 1.1%
      [PropertyType.Villa]: 0.011, // 빌라: 1.1%
      [PropertyType.Officetel]: 0.046, // 오피스텔: 4.6%
      [PropertyType.MultiHouse]: 0.011, // 다가구주택: 1.1%
      [PropertyType.Detached]: 0.011, // 단독주택: 1.1%
      [PropertyType.ResidentialLand]: 0.011, // 주거용 토지: 1.1%

      // 주거용 (크롤링 추가)
      [PropertyType.MultiFamily]: 0.011, // 다세대: 1.1%
      [PropertyType.RowHouse]: 0.011, // 연립주택: 1.1%
      [PropertyType.MixedUse]: 0.046, // 주상복합: 4.6% (상업용으로 분류)

      // 비주거용 (상가/토지 4.6%)
      [PropertyType.Store]: 0.046, // 상가: 4.6%
      [PropertyType.Office]: 0.046, // 사무실: 4.6%
      [PropertyType.Factory]: 0.046, // 공장: 4.6%
      [PropertyType.Warehouse]: 0.046, // 창고: 4.6%
      [PropertyType.CommercialLand]: 0.046, // 상업용 토지: 4.6%

      // 상업용 (크롤링 추가)
      [PropertyType.StoreHouse]: 0.046, // 상가주택: 4.6%
      [PropertyType.NeighborhoodFacility]: 0.046, // 근린생활시설: 4.6%
      [PropertyType.RetailFacility]: 0.046, // 판매시설: 4.6%
      [PropertyType.SportsFacility]: 0.046, // 운동시설: 4.6%
      [PropertyType.LodgingFacility]: 0.046, // 숙박시설: 4.6%
    },

    // 기본값 (매물 유형이 없거나 매칭되지 않을 때)
    acquisitionTaxRateDefault: 0.046, // 4.6% (상가/토지 기본값)

    legalFeeFlat: 900000, // 하위 호환성 유지
    legalFeeRate: 0.007, // 입찰가의 0.7% (legalFeeRate 우선 사용)

    repairRate: 0.03, // 감정가 대비 3%
    holdingMonthsDefault: 6, // 보유 6개월 기준
    holdingMonthlyRate: 0.0009, // 월 보유비용 비율 (userBid의 0.09%)

    loanLtvDefault: 0.7,
    loanInterestRate: 0.045, // 연 4.5%
    loanRate: 0.045, // 대출 이자율 (loanInterestRate와 동일)

    /** 양도소득세 정책 */
    transferTax: {
      ratesByHoldingPeriod: {
        underOneYear: 0.77, // 1년 미만: 77% 중과세
        oneToTwoYears: 0.35, // 1-2년: 35% 기본세율
        overTwoYears: 0.0, // 2년 이상: 0% 비과세
      },
      includeLocalTax: true, // 지방소득세 포함
    },

    /** 부대 비용 관련 정책 */
    additionalCosts: {
      /** 국민주택채권 할인율 범위 */
      bondDiscountRateRange: {
        min: 0.1, // 10%
        max: 0.15, // 15%
      },
      /** 미납관리비 예상 기간 (개월) */
      unpaidMaintenanceFeeMonths: 6, // 6개월 (평당 7천원 기준)

      /** 청구금액 정책 */
      claimAmount: {
        /** 난이도별 현실적인 청구금액 범위 (감정가 대비) */
        ratioRangeByDifficulty: {
          easy: { min: 0.00, max: 0.50 },     // 0-50%: 학습용 제한
          normal: { min: 0.10, max: 0.70 },   // 10-70%: 일반 권리관계
          hard: { min: 0.30, max: 0.90 },     // 30-90%: 복잡 권리관계
        },

        /** 매물 유형별 승수 (현실성 강화) */
        multiplierByType: {
          // 주거용: 상대적으로 낮음
          [PropertyType.Apartment]: 0.8,       // 대지지분/대지권으로 청구금액 적음
          [PropertyType.Villa]: 1.0,           // 기본 승수
          [PropertyType.Detached]: 1.2,        // 단독주택: 토지 지분 복잡

          // 상업용: 임차인이 많아 청구금액 높음
          [PropertyType.Store]: 1.5,           // 상가: 임차인 보증금 많음
          [PropertyType.Office]: 1.3,          // 사무실: 기업 임차
          [PropertyType.CommercialLand]: 1.4,  // 상업지: 개발 기대 배당
        },

        /** 경매 회차별 추가 승수 */
        auctionStepBonus: {
          base: 1.0,       // 1회차 기준
          perStep: 0.08,   // 회차당 8% 추가 (법원 비용 누적)
        },

        /** 청구금액 0원 확률 (학습용 단순 매물 비율) */
        zeroClaimProbability: 0.15,  // 15%는 청구금액 없음

        /** 최대 청구금액 제한 (감정가 대비 비율) */
        maxRatioLimit: 1.2,  // 감정가의 120%까지 허용 (초과 배당 가능)
      },
    },

    /** 자금 투입 스케줄 정책 */
    fundingSchedule: {
      depositRate: 0.1, // 보증금 10%
      paymentDays: {
        deposit: 0, // 입찰 당일
        balance: 45, // 낙찰 +45일
        taxAndLegal: 50, // 낙찰 +50일
        evictionAndRepair: 70, // 낙찰 +70일
      },
    },
  },

  /* ======================================================
   * 4) Profit (수익 목표 기준)
   * ====================================================== */
  profit: {
    targetMarginRate: 0.08, // 초기 안전마진 목표 8%
    targetAnnualRoi: 0.1, // 연환산 ROI 10%
    maxRoi: 10, // ROI 상한선 (1000%)
    maxAnnualizedRoi: 50, // 연환산 ROI 상한선 (5000%)
    gradeToScore: {
      S: 100,
      A: 90,
      B: 80,
      C: 70,
      D: 60,
    },
  },

  /* ======================================================
   * 5) Competitor (경쟁자 시뮬레이션 정책)
   * ====================================================== */
  competitor: {
    /** 경쟁자 수 (Normal 모드 기본값)
     * - 현재 난이도 정책 기준: 4명
     * - 현실적인 경쟁 환경 제공
     * - v2.3: 동적 조정 기능 추가 (매물 유형, 권리 상황, 난이도 반영)
     */
    count: 4, // 기본값 (하위 호환성 유지)

    /** 매물 유형별 기본 경쟁자 수 (동적 조정용)
     * - 실제 시장 반영: 아파트 > 빌라 > 상가 순으로 경쟁 강도 차이
     * - min: 최소 경쟁자 수, max: 최대 경쟁자 수, avg: 평균 경쟁자 수
     */
    countByPropertyType: {
      [PropertyType.Apartment]: { min: 3, max: 8, avg: 5 }, // 아파트: 인기 높음
      [PropertyType.Villa]: { min: 2, max: 6, avg: 4 },
      [PropertyType.Officetel]: { min: 2, max: 6, avg: 4 },
      [PropertyType.MultiHouse]: { min: 1, max: 5, avg: 3 },
      [PropertyType.Detached]: { min: 1, max: 4, avg: 2.5 },
      [PropertyType.ResidentialLand]: { min: 1, max: 4, avg: 2.5 },

      // 주거용 (크롤링 추가)
      [PropertyType.MultiFamily]: { min: 2, max: 6, avg: 4 }, // 다세대: Villa와 유사
      [PropertyType.RowHouse]: { min: 2, max: 6, avg: 4 }, // 연립주택: Villa와 유사
      [PropertyType.MixedUse]: { min: 2, max: 6, avg: 4 }, // 주상복합: Officetel과 유사

      [PropertyType.Store]: { min: 1, max: 4, avg: 2 }, // 상가: 경쟁 낮음
      [PropertyType.Office]: { min: 1, max: 3, avg: 2 },
      [PropertyType.Factory]: { min: 1, max: 3, avg: 2 },
      [PropertyType.Warehouse]: { min: 1, max: 3, avg: 2 },
      [PropertyType.CommercialLand]: { min: 1, max: 3, avg: 2 },

      // 상업용 (크롤링 추가)
      [PropertyType.StoreHouse]: { min: 1, max: 4, avg: 2 }, // 상가주택: Store와 유사
      [PropertyType.NeighborhoodFacility]: { min: 1, max: 3, avg: 2 }, // 근린생활시설
      [PropertyType.RetailFacility]: { min: 1, max: 4, avg: 2 }, // 판매시설: Store와 유사
      [PropertyType.SportsFacility]: { min: 1, max: 3, avg: 2 }, // 운동시설
      [PropertyType.LodgingFacility]: { min: 1, max: 4, avg: 2.5 }, // 숙박시설: 약간 높음
    },

    /** 권리 상황에 따른 경쟁자 수 감소율
     * - 리스크가 높을수록 경쟁자가 급격히 감소 (실제 시장 반영)
     * - low: 리스크 낮음 (evictionRisk < 0.3, assumableFactor < 0.1)
     * - medium: 리스크 중간 (그 외)
     * - high: 리스크 높음 (evictionRisk > 0.7 또는 assumableFactor > 0.2)
     */
    countReductionByRisk: {
      low: 1.0, // 리스크 낮음: 100% 유지 (감소 없음)
      medium: 0.7, // 리스크 중간: 30% 감소
      high: 0.4, // 리스크 높음: 60% 감소
    },

    /** 난이도별 경쟁자 수 조정
     * - Easy: -1~2명 감소 (학습 목적)
     * - Normal: 기본값 유지
     * - Hard: +1~2명 증가 (도전 목적)
     */
    countAdjustmentByDifficulty: {
      easy: -1.5, // 평균 1.5명 감소
      normal: 0, // 조정 없음
      hard: +1.5, // 평균 1.5명 증가
    },

    /** 실제 통계 기반 낙찰가율 (감정가 대비, v2.3)
     * - 한국감정원 및 법원 경매 통계 기반
     * - 매물 유형별 평균 낙찰가율 반영
     * - 실제 시장과 유사한 입찰가 범위 제공
     */
    actualBidRateByPropertyType: {
      [PropertyType.Apartment]: {
        mean: 0.88, // 아파트: 평균 88% (감정가 대비)
        stdDev: 0.05, // 표준편차 5%
        min: 0.75, // 최소 75%
        max: 1.05, // 최대 105% (감정가 초과는 드묾)
      },
      [PropertyType.Villa]: {
        mean: 0.82, // 빌라: 평균 82%
        stdDev: 0.08, // 표준편차 8%
        min: 0.7, // 최소 70%
        max: 1.0, // 최대 100%
      },
      [PropertyType.Officetel]: {
        mean: 0.85, // 오피스텔: 평균 85%
        stdDev: 0.06, // 표준편차 6%
        min: 0.72, // 최소 72%
        max: 1.02, // 최대 102%
      },
      [PropertyType.MultiHouse]: {
        mean: 0.8, // 다가구: 평균 80%
        stdDev: 0.09, // 표준편차 9%
        min: 0.65, // 최소 65%
        max: 0.98, // 최대 98%
      },
      [PropertyType.Detached]: {
        mean: 0.78, // 단독주택: 평균 78%
        stdDev: 0.1, // 표준편차 10%
        min: 0.6, // 최소 60%
        max: 0.95, // 최대 95%
      },
      [PropertyType.ResidentialLand]: {
        mean: 0.75, // 주거용 토지: 평균 75%
        stdDev: 0.12, // 표준편차 12%
        min: 0.55, // 최소 55%
        max: 0.92, // 최대 92%
      },

      // 주거용 (크롤링 추가)
      [PropertyType.MultiFamily]: {
        mean: 0.82, // 다세대: 평균 82% (Villa와 유사)
        stdDev: 0.08, // 표준편차 8%
        min: 0.7, // 최소 70%
        max: 1.0, // 최대 100%
      },
      [PropertyType.RowHouse]: {
        mean: 0.82, // 연립주택: 평균 82% (Villa와 유사)
        stdDev: 0.08, // 표준편차 8%
        min: 0.7, // 최소 70%
        max: 1.0, // 최대 100%
      },
      [PropertyType.MixedUse]: {
        mean: 0.85, // 주상복합: 평균 85% (Officetel과 유사)
        stdDev: 0.06, // 표준편차 6%
        min: 0.72, // 최소 72%
        max: 1.02, // 최대 102%
      },

      [PropertyType.Store]: {
        mean: 0.75, // 상가: 평균 75% (인기 낮음)
        stdDev: 0.1, // 표준편차 10%
        min: 0.6, // 최소 60%
        max: 0.95, // 최대 95%
      },
      [PropertyType.Office]: {
        mean: 0.78, // 사무실: 평균 78%
        stdDev: 0.09, // 표준편차 9%
        min: 0.62, // 최소 62%
        max: 0.96, // 최대 96%
      },
      [PropertyType.Factory]: {
        mean: 0.72, // 공장: 평균 72%
        stdDev: 0.11, // 표준편차 11%
        min: 0.55, // 최소 55%
        max: 0.9, // 최대 90%
      },
      [PropertyType.Warehouse]: {
        mean: 0.7, // 창고: 평균 70%
        stdDev: 0.12, // 표준편차 12%
        min: 0.52, // 최소 52%
        max: 0.88, // 최대 88%
      },
      [PropertyType.CommercialLand]: {
        mean: 0.73, // 상업용 토지: 평균 73%
        stdDev: 0.11, // 표준편차 11%
        min: 0.58, // 최소 58%
        max: 0.93, // 최대 93%
      },

      // 상업용 (크롤링 추가)
      [PropertyType.StoreHouse]: {
        mean: 0.75, // 상가주택: 평균 75% (Store와 유사)
        stdDev: 0.1, // 표준편차 10%
        min: 0.6, // 최소 60%
        max: 0.95, // 최대 95%
      },
      [PropertyType.NeighborhoodFacility]: {
        mean: 0.74, // 근린생활시설: 평균 74%
        stdDev: 0.1, // 표준편차 10%
        min: 0.59, // 최소 59%
        max: 0.94, // 최대 94%
      },
      [PropertyType.RetailFacility]: {
        mean: 0.75, // 판매시설: 평균 75% (Store와 유사)
        stdDev: 0.1, // 표준편차 10%
        min: 0.6, // 최소 60%
        max: 0.95, // 최대 95%
      },
      [PropertyType.SportsFacility]: {
        mean: 0.72, // 운동시설: 평균 72% (Factory와 유사)
        stdDev: 0.11, // 표준편차 11%
        min: 0.55, // 최소 55%
        max: 0.9, // 최대 90%
      },
      [PropertyType.LodgingFacility]: {
        mean: 0.76, // 숙박시설: 평균 76% (Store보다 약간 높음)
        stdDev: 0.09, // 표준편차 9%
        min: 0.62, // 최소 62%
        max: 0.96, // 최대 96%
      },
    },

    /** 권리 상황별 낙찰가율 조정 (v2.3) - 하위 호환성 유지
     * - 실제 통계 기반 낙찰가율에 권리 상황에 따른 조정값 적용
     * - low: 리스크 낮음 → +2%p 상향 (안전한 매물은 더 높게 입찰)
     * - medium: 리스크 중간 → 0%p (기본값 유지)
     * - high: 리스크 높음 → -8%p 하향 (위험한 매물은 더 낮게 입찰)
     */
    bidRateAdjustmentByRisk: {
      low: +0.02, // 리스크 낮음: +2%p 상향
      medium: 0.0, // 리스크 중간: 조정 없음
      high: -0.08, // 리스크 높음: -8%p 하향
    },

    /** 위험도별 낙찰가율 통계 (0~5, v2.3)
     * - Rights.breakdown의 최대 risk 값을 기반으로 낙찰가율 결정
     * - 위험도가 높을수록 낙찰가율이 낮아짐 (시장 현실 반영)
     * - 실제 부동산 경매 통계 기반 추정값
     */
    actualBidRateByRiskLevel: {
      0: {
        mean: 0.9, // 위험 없음: 평균 90%
        stdDev: 0.04, // 표준편차 4%
        min: 0.8, // 최소 80%
        max: 1.05, // 최대 105%
      },
      1: {
        mean: 0.88, // 매우 낮음 (근저당권 등): 평균 88%
        stdDev: 0.05, // 표준편차 5%
        min: 0.75, // 최소 75%
        max: 1.05, // 최대 105%
      },
      2: {
        mean: 0.85, // 낮음 (가처분, 지역권 등): 평균 85%
        stdDev: 0.06, // 표준편차 6%
        min: 0.7, // 최소 70%
        max: 1.0, // 최대 100%
      },
      3: {
        mean: 0.75, // 중간 (미보증금 세입자, 가등기 등): 평균 75%
        stdDev: 0.08, // 표준편차 8%
        min: 0.6, // 최소 60%
        max: 0.95, // 최대 95%
      },
      4: {
        mean: 0.7, // 높음 (임차권, 법정지상권 등): 평균 70%
        stdDev: 0.1, // 표준편차 10%
        min: 0.55, // 최소 55%
        max: 0.9, // 최대 90%
      },
      5: {
        mean: 0.6, // 매우 높음 (대항력 세입자, 유치권 등): 평균 60%
        stdDev: 0.12, // 표준편차 12%
        min: 0.45, // 최소 45%
        max: 0.85, // 최대 85%
      },
    },

    /** 지역별 낙찰가율 조정값 (v2.3)
     * - 위험도별 낙찰가율에 지역별 조정값을 추가로 적용
     * - 단위: %p (percentage point, 예: +0.05 = +5%p)
     * - 실제 시장 통계 기반: 수도권은 수요 높아 낙찰가율 상향, 지방은 상대적으로 하향
     */
    bidRateAdjustmentByRegion: {
      서울: +0.05, // 서울: +5%p (수요 높음, 경쟁 치열)
      경기: +0.03, // 경기: +3%p (수요 높음)
      인천: 0.0, // 인천: 조정 없음
      부산: -0.02, // 부산: -2%p (수요 상대적으로 낮음)
      대구: -0.03, // 대구: -3%p
      광주: -0.04, // 광주: -4%p
    },
    /** 경쟁자 입찰가 범위 (minBid 대비 %)
     * - 개선: 90%~125%로 확대하여 입찰가 다양성 확보
     * - 현실적인 경매 환경: 일부는 보수적, 일부는 공격적으로 입찰
     */
    bidRange: { min: 0.9, max: 1.25 }, // minBid 대비 90%~125%
    /** 난이도별 경쟁 강도 배수
     * - Easy: 0.6 (경쟁 강도 60%) - 범위 폭 축소로 보수적 입찰 유도
     * - Normal: 1.0 (경쟁 강도 100%) - 기본 범위 유지
     * - Hard: 1.5 (경쟁 강도 150%) - 범위 폭 확대로 공격적 입찰 가능
     *
     * 적용 방식 (generateCompetitorBids 함수):
     * adjustedRangeMax = rangeMin + (rangeMax - rangeMin) * difficultyMultiplier
     *
     * 이 배수는 입찰가 범위의 "폭"을 조절하여 난이도별 경쟁 강도를 차등화합니다.
     */
    difficultyMultiplier: {
      easy: 0.6, // Easy: 경쟁 강도 60%
      normal: 1.0, // Normal: 경쟁 강도 100%
      hard: 1.5, // Hard: 경쟁 강도 150%
    },
    /** 경쟁자 입찰가 분포 타입
     * - "normal": 정규 분포 (Box-Muller 변환 사용)
     * - "uniform": 균등 분포
     * - "skewed": 왜도 분포 (향후 확장 예정)
     */
    distributionType: "normal" as const, // 정규 분포
  },

  /* ======================================================
   * 6) Land (토지 정책)
   * ====================================================== */
  land: {
    /** 토지 지분 생성 확률 (유형별) */
    shareRatioProbability: {
      [PropertyType.Apartment]: { fullShare: 0, partialShare: 0 }, // 아파트는 토지 지분 없음 (대지권만)
      [PropertyType.Villa]: { fullShare: 0.7, partialShare: 0.3 }, // 빌라: 70% 전부, 30% 분할
      [PropertyType.Officetel]: { fullShare: 0, partialShare: 0 }, // 오피스텔은 토지 지분 없음 (대지권만)
      [PropertyType.MultiHouse]: { fullShare: 0.5, partialShare: 0.5 }, // 다가구: 50% 전부, 50% 분할
      [PropertyType.Detached]: { fullShare: 0.9, partialShare: 0.1 }, // 단독: 90% 전부, 10% 분할
      [PropertyType.ResidentialLand]: { fullShare: 1.0, partialShare: 0 }, // 토지 단독: 항상 전부

      // 주거용 (크롤링 추가)
      [PropertyType.MultiFamily]: { fullShare: 0.7, partialShare: 0.3 }, // 다세대: Villa와 유사
      [PropertyType.RowHouse]: { fullShare: 0.7, partialShare: 0.3 }, // 연립주택: Villa와 유사
      [PropertyType.MixedUse]: { fullShare: 0, partialShare: 0 }, // 주상복합: Officetel과 유사 (대지권만)

      [PropertyType.Store]: { fullShare: 0.8, partialShare: 0.2 },
      [PropertyType.Office]: { fullShare: 0.8, partialShare: 0.2 },
      [PropertyType.Factory]: { fullShare: 0.9, partialShare: 0.1 },
      [PropertyType.Warehouse]: { fullShare: 0.9, partialShare: 0.1 },
      [PropertyType.CommercialLand]: { fullShare: 1.0, partialShare: 0 },

      // 상업용 (크롤링 추가)
      [PropertyType.StoreHouse]: { fullShare: 0.8, partialShare: 0.2 }, // 상가주택: Store와 유사
      [PropertyType.NeighborhoodFacility]: {
        fullShare: 0.8,
        partialShare: 0.2,
      }, // 근린생활시설
      [PropertyType.RetailFacility]: { fullShare: 0.8, partialShare: 0.2 }, // 판매시설: Store와 유사
      [PropertyType.SportsFacility]: { fullShare: 0.9, partialShare: 0.1 }, // 운동시설: Factory와 유사
      [PropertyType.LodgingFacility]: { fullShare: 0.8, partialShare: 0.2 }, // 숙박시설: Store와 유사
    },

    /** 대지권 비율 생성 확률 (아파트/오피스텔) */
    surfaceRightRatioProbability: {
      full: 0.8, // 80% 전부 대지권
      partial: 0.2, // 20% 분할 대지권
    },

    /** 토지 감정가 계산 비율 (건물 감정가 대비) */
    landAppraisalRatePerType: {
      [PropertyType.Apartment]: 0.25, // 아파트: 건물 대비 25%
      [PropertyType.Villa]: 0.4, // 빌라: 건물 대비 40%
      [PropertyType.Officetel]: 0.2, // 오피스텔: 건물 대비 20%
      [PropertyType.MultiHouse]: 0.45, // 다가구: 건물 대비 45%
      [PropertyType.Detached]: 0.5, // 단독: 건물 대비 50%
      [PropertyType.ResidentialLand]: 1.0, // 토지 단독: 100%

      // 주거용 (크롤링 추가)
      [PropertyType.MultiFamily]: 0.4, // 다세대: Villa와 유사
      [PropertyType.RowHouse]: 0.4, // 연립주택: Villa와 유사
      [PropertyType.MixedUse]: 0.2, // 주상복합: Officetel과 유사

      [PropertyType.Store]: 0.35,
      [PropertyType.Office]: 0.3,
      [PropertyType.Factory]: 0.6,
      [PropertyType.Warehouse]: 0.55,
      [PropertyType.CommercialLand]: 1.0,

      // 상업용 (크롤링 추가)
      [PropertyType.StoreHouse]: 0.35, // 상가주택: Store와 유사
      [PropertyType.NeighborhoodFacility]: 0.35, // 근린생활시설
      [PropertyType.RetailFacility]: 0.35, // 판매시설: Store와 유사
      [PropertyType.SportsFacility]: 0.6, // 운동시설: Factory와 유사
      [PropertyType.LodgingFacility]: 0.35, // 숙박시설: Store와 유사
    },

    /** 토지 용도별 가중치 */
    landUseWeights: {
      대지: 0.7, // 70%
      전: 0.2, // 20%
      잡종지: 0.1, // 10%
    },
  },

  /* ======================================================
   * 7) Excluded Building (제시외 건물 정책)
   * ====================================================== */
  excludedBuilding: {
    /** 제시외 건물 생성 확률 (유형별) */
    generationProbability: {
      [PropertyType.Apartment]: 0.1, // 아파트: 10%
      [PropertyType.Villa]: 0.3, // 빌라: 30%
      [PropertyType.Officetel]: 0.15, // 오피스텔: 15%
      [PropertyType.MultiHouse]: 0.4, // 다가구: 40%
      [PropertyType.Detached]: 0.5, // 단독: 50%
      [PropertyType.ResidentialLand]: 0, // 토지 단독: 0%

      // 주거용 (크롤링 추가)
      [PropertyType.MultiFamily]: 0.3, // 다세대: Villa와 유사
      [PropertyType.RowHouse]: 0.3, // 연립주택: Villa와 유사
      [PropertyType.MixedUse]: 0.15, // 주상복합: Officetel과 유사

      [PropertyType.Store]: 0.2,
      [PropertyType.Office]: 0.1,
      [PropertyType.Factory]: 0.3,
      [PropertyType.Warehouse]: 0.4,
      [PropertyType.CommercialLand]: 0,

      // 상업용 (크롤링 추가)
      [PropertyType.StoreHouse]: 0.2, // 상가주택: Store와 유사
      [PropertyType.NeighborhoodFacility]: 0.2, // 근린생활시설
      [PropertyType.RetailFacility]: 0.2, // 판매시설: Store와 유사
      [PropertyType.SportsFacility]: 0.3, // 운동시설: Factory와 유사
      [PropertyType.LodgingFacility]: 0.25, // 숙박시설: 약간 높음
    },

    /** 제시외 건물 종류별 가중치 */
    typeWeights: {
      창고: 0.4, // 40%
      차고: 0.3, // 30%
      부속건물: 0.2, // 20%
      옥외창고: 0.1, // 10%
    },

    /** 제시외 건물 감정가 계산 비율 (건물 감정가 대비) */
    appraisalRate: 0.15, // 건물 감정가의 15% (면적 비율 추가 적용)
  },
};

export default defaultPolicy;
